import SkeletonBox from '@src/components/common/SkeletonBox';
import { SkeletonParagraph } from '@src/components/common/SkeletonDocument';
import {
  CURSOR_ALLOWANCE_PX,
  TOPIC_RAIL_GUTTER_PX,
} from '@src/components/transcript/topicRailStyles';
import styled from 'styled-components';

import {
  RIGHT_COLUMN_SPAN,
  RIGHT_COLUMN_WIDTH,
  TranscriptTabContainer,
  WAVEFORM_EXPANDED_HEIGHT,
} from './transcriptTabLayout';

import type { FC } from 'react';

/** Widths of the header's filter placeholders (Quotes, Clips, Highlights). */
const FILTER_WIDTHS = ['72px', '60px', '92px'];

/** Line counts per paragraph: enough to fill the pane on a typical viewport. */
const PARAGRAPH_LINES = [4, 3, 5, 2, 4, 3];

/** Speaker-name widths, so the headers read as different people. */
const SPEAKER_WIDTHS = ['96px', '124px', '84px'];

/** Rows in the right column's list (Co-Producer or Speakers). */
const LIST_ROWS = 4;

type Props = {
  /** Translation reviewers get no right column: transcript and waveform run
   *  full width, as on the loaded tab. */
  reviewerMode?: boolean;
  /** The Co-Producer | Speakers strip only renders when clip chat is on. */
  showRightTabs?: boolean;
};

/**
 * The Transcript tab while the story's transcript loads (PLAT-12125): the
 * same transcript pane, waveform card and player column the tab paints once
 * the transcript lands, positioned from the same `transcriptTabLayout`
 * numbers, so nothing moves when it does.
 *
 * It stands in only until the transcript text is readable — the waveform,
 * languages, topics and player each fill their own region inside the real tab
 * after that. When the tab's layout changes, this changes with it.
 */
export const TranscriptTabSkeleton: FC<Props> = ({
  reviewerMode = false,
  showRightTabs = false,
}) => (
  <TranscriptTabContainer data-testid="transcript-tab-skeleton" aria-hidden>
    <Pane $reviewerMode={reviewerMode}>
      <Header>
        {FILTER_WIDTHS.map(width => (
          <SkeletonBox
            key={width}
            $width={width}
            $height="24px"
            $radius="6px"
          />
        ))}
        <HeaderTail>
          <SkeletonBox $width="148px" $height="24px" $radius="6px" />
        </HeaderTail>
      </Header>
      <Body>
        {PARAGRAPH_LINES.map((lines, i) => (
          <Paragraph key={i}>
            <SpeakerRow>
              <SkeletonBox
                $width={SPEAKER_WIDTHS[i % SPEAKER_WIDTHS.length]}
                $height="12px"
              />
              <SkeletonBox $width="36px" $height="10px" />
            </SpeakerRow>
            <SkeletonParagraph lines={lines} lineHeight="14px" />
          </Paragraph>
        ))}
      </Body>
    </Pane>

    <Waveform $reviewerMode={reviewerMode}>
      <SkeletonBox $height="100%" $radius="var(--radius-lg, 12px)" />
    </Waveform>

    {!reviewerMode && (
      <RightColumn data-testid="transcript-tab-skeleton-column">
        <Player />
        <Transport>
          <SkeletonBox $width="16px" $height="16px" $radius="50%" />
          <SkeletonBox $height="4px" $radius="2px" />
          <SkeletonBox $width="64px" $height="10px" />
        </Transport>
        {showRightTabs && (
          <Tabs data-testid="transcript-tab-skeleton-tabs">
            <SkeletonBox $width="84px" $height="14px" />
            <SkeletonBox $width="64px" $height="14px" />
          </Tabs>
        )}
        <List>
          {Array.from({ length: LIST_ROWS }, (_, i) => (
            <ListRow key={i}>
              <SkeletonBox $width="28px" $height="28px" $radius="50%" />
              <SkeletonBox
                $width={i % 2 === 0 ? '60%' : '45%'}
                $height="12px"
              />
            </ListRow>
          ))}
        </List>
      </RightColumn>
    )}
  </TranscriptTabContainer>
);

// TranscriptPane's box: right of it the column, below it the waveform card.
// Top inset is Transcript Main's padding-top; left is the transcript's
// topic-rail gutter, where its toolbar and text start.
const Pane = styled.div<{ $reviewerMode: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: ${props => (props.$reviewerMode ? 0 : RIGHT_COLUMN_SPAN)}px;
  bottom: ${WAVEFORM_EXPANDED_HEIGHT}px;
  overflow: hidden;
  padding: 12px var(--spacing-md) 0
    ${TOPIC_RAIL_GUTTER_PX + CURSOR_ALLOWANCE_PX}px;
  display: flex;
  flex-direction: column;
`;

// Transcript's header row (`transcriptLayoutStyles` Header): 32px, 8px below.
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 32px;
  flex-shrink: 0;
  margin-bottom: 8px;
`;

const HeaderTail = styled.div`
  margin-left: auto;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 4px;
  padding-right: 14px;
`;

const Paragraph = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// SpeakerHeader: the name on the left, the paragraph's start time on the right.
const SpeakerRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Waveform = styled.div<{ $reviewerMode: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: ${props => (props.$reviewerMode ? 0 : RIGHT_COLUMN_SPAN)}px;
  height: ${WAVEFORM_EXPANDED_HEIGHT}px;
`;

const RightColumn = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${RIGHT_COLUMN_WIDTH}px;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
`;

const Player = styled(SkeletonBox)`
  flex-shrink: 0;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  border-radius: 0;
`;

const Transport = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px;
  padding: 0 12px;

  /* Play button and time keep their size; the scrub bar takes the rest. */
  & > * {
    flex-shrink: 0;
  }
  & > :nth-child(2) {
    flex: 1;
  }
`;

const Tabs = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 24px;
  height: 40px;
  padding: 0 20px;
  border-bottom: 1px solid var(--border);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 12px;
`;

const ListRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
